import { Customer, INVOICE_ACCOUNT_DENIED, INVOICE_ACCOUNT_TEMPORARY, LicenseType, permissionAllowed, SystemLicense } from "@ews/react-data";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import
{
    useFetchSystemTextMessages,
    useModifySystemLicense,
    useSystem,
    useSystemLicense
} from "../../../ReactData/system";

import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonCheckbox, IonItem, IonLabel, IonText } from "@ionic/react";
import React from "react";
import { Locale } from "../../../../setup/locale";
import { injectHTML as h } from "../../../../utils";
import CustomerTypeAhead from "../../../Customer/CustomerTypeAhead";
import { useDoubleConfirmDialog } from "../../../Dialog";
import { useMyDateFormat, useMyProfileId } from "../../../Login/LoginProvider";
import MonthPicker from "../../../MonthPicker";
import PriceListTable from "../../../PriceList/PriceListTable";
import { useModifyCustomer } from "../../../ReactData/customer";
import { useInvokeToggleCollectiveInvoice } from "../../../ReactData/rpc";
import { useUser, useUserPermissions } from "../../../ReactData/user";
import { useIsMobile } from "../../../Responsive";
import SettingsCol from "../../../Settings/SettingsCol/SettingsCol";
import SettingsCheckbox from "../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox";
import SettingsLabel from "../../../Settings/SettingsComponents/SettingsLabel/SettingsLabel";
import SettingsTextInput from "../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { TooltipIcon } from "../../../ToolTip/useTooltip";
import { InvoiceAddressPopover } from "./components/InvoiceAddressPopover/InvoiceAddressPopover";
import { LSTCompanyChoicePopover } from "./components/LSTCompanyChoicePopover/LSTCompanyChoicePopover";
import { convertIntervalToInt } from "./helper";
import style from "./LicenseSettings.module.scss";
import { LicenseSettingsProps } from "./types";


type License = {
    type: number;
    text?: string;
    editable?: boolean;
    value: LicenseType;
};

const max = Object.values(LicenseType).filter(v => !isNaN(Number(v))).reduce((a, c) => Number(a) | Number(c), 0);

type LicenseCheckboxProps = {
    disabled?: boolean,
    licenseRequested?: boolean,
    licenseActive?: boolean;
    slot?: string;
    onChange?: (checked: boolean) => void;
};

type LicenseCheckboxLabelProps = {
    textLicenseRequested: string;
    textLicenseActive: string;
    slot?: string;
};

const LicenseCheckboxLabel: FC<LicenseCheckboxLabelProps> = ({
    textLicenseRequested = '',
    textLicenseActive = '',
    slot = 'end'
}) =>
{
    return <IonItem
        slot={slot}
        style={{
            alignItems: "baseline",
            paddingTop: "5px"
        }}
        lines="none"
    >
        <IonText
            color={'medium'}
            style={{
                writingMode: "sideways-lr",
                marginRight: "1.5em",
                textAlign: "left",
                width: "2em"
            }}
        >
            {h(textLicenseActive)}
        </IonText>
        <IonText
            color={'medium'}
            style={{
                writingMode: "sideways-lr",
                textAlign: "left",
                width: "2em"
            }}
        >
            {h(textLicenseRequested)}
        </IonText>
    </IonItem>;
};

const LicenseCheckbox: FC<LicenseCheckboxProps> = ({
    disabled = false,
    licenseRequested = false,
    licenseActive = false,
    slot = 'end',
    onChange
}) =>
{
    return <IonItem
        lines="none"
        slot={slot}
    >
        <IonCheckbox
            disabled
            style={{
                marginRight: "1em",
                width: "2em"
            }}
            checked={licenseActive}
        />
        <IonCheckbox
            style={{
                width: "2em"
            }}
            disabled={disabled}
            checked={licenseRequested}
            onIonChange={(e) => onChange && onChange(e.detail.checked)}
        />

    </IonItem>;

    // return <SettingsCheckbox

    //                 lines="none"
    //                 disabled={!editable || isTrialLicense(systemLicense.license!)}
    //                 text={text as Locale}
    //                 checked={(currentLicenseType & value) === value}
    //                 onChange={(checked) => listLicense(license, checked)}
    //             />;
};

const LicenseSettings: React.FC<LicenseSettingsProps> = ({ systemId }) =>
{
    const mobileView = useIsMobile();
    const { translate: t } = useL10n();

    const system = useSystem(systemId);
    const systemLicense = useSystemLicense(systemId);
    const dialog = useDoubleConfirmDialog();
    const fetchTextMessageRecipients = useFetchSystemTextMessages();
    const formatDate = useMyDateFormat();

    const _modifySystemLicense = useModifySystemLicense();

    const modifyCustomer = useModifyCustomer();
    const toggleCollectiveInvoice = useInvokeToggleCollectiveInvoice();

    const me = useMyProfileId();
    const currentUser = useUser(me);

    const userPermission = useUserPermissions(me);
    const licenseRequested = useMemo(() => systemLicense?.licenseRequested || LicenseType.NONE, [systemLicense.licenseRequested]);
    const expireRef = useRef<HTMLIonInputElement>(null);
    const [manualExpire, setManualExpire] = useState<string | null>(null);

    const [LSTCompanyLocationPopoverIsOpen, setLSTCompanyLocationPopoverIsOpen] = useState<boolean>(false);
    const [InvoiceAddressPopoverIsOpen, setInvoiceAddressPopoverIsOpen] = useState<boolean>(false);
    const [currentLicenseType, setCurrentLicenseType] = useState<number>(licenseRequested);
    const [companyLocation, setCompanyLocation] = useState<Record<string, any>>({});
    const [textMessageRecipients, setTextMessageRecipients] = useState<number>(0);

    const userIsFromPayingCustomer = useMemo(() => (systemLicense.customerId && currentUser.customerId === systemLicense.customerId), [currentUser.customerId, systemLicense.customerId]);
    const isABF = useMemo(() => permissionAllowed(userPermission.isAdmin!) && (userPermission.licenseManagement || false), [userPermission.isAdmin, userPermission.licenseManagement]);
    const existCustomerNumberLST = useMemo(() => systemLicense.invoiceAccountNumber !== null && systemLicense.invoiceAccountNumber !== undefined, [systemLicense.invoiceAccountNumber]);
    const deniedCustomerNumberLST = useMemo(() => typeof systemLicense.invoiceAccountNumber === "string" && systemLicense.invoiceAccountNumber === INVOICE_ACCOUNT_DENIED, [systemLicense.invoiceAccountNumber]);
    const validCustomerNumberLST = useMemo(() => typeof systemLicense.invoiceAccountNumber === "string" && ((systemLicense.invoiceAccountNumber || "") as string).length >= 6 && existCustomerNumberLST && !deniedCustomerNumberLST, [deniedCustomerNumberLST, existCustomerNumberLST, systemLicense.invoiceAccountNumber]);
    const hasLicense = useMemo(() => ((currentLicenseType || LicenseType.NONE) & max) !== LicenseType.NONE, [currentLicenseType]);
    const hasCustomerId = useMemo(() => Boolean(systemLicense?.customerId), [systemLicense?.customerId]);
    const hasCustomerIdAndCustomerNumber = useMemo(() => (hasCustomerId && validCustomerNumberLST), [hasCustomerId, validCustomerNumberLST]);
    const LicenseWillNotBeCharged = useMemo(() => (!hasCustomerIdAndCustomerNumber), [hasCustomerIdAndCustomerNumber]);  // TODO: hier noch überlegen wann das true sein sol,[])
    const showLicenseWillNotBeChargedCB = useMemo(() => !hasCustomerIdAndCustomerNumber && isABF, [hasCustomerIdAndCustomerNumber, isABF]);
    const canStartTestVersion = useMemo(() => Boolean(permissionAllowed(userPermission.grantSystemAccess!) && validCustomerNumberLST), [userPermission.grantSystemAccess, validCustomerNumberLST]);
    const canEditLicenses = useMemo(() => Boolean(
        userIsFromPayingCustomer
        && validCustomerNumberLST
    ), [userIsFromPayingCustomer, validCustomerNumberLST]);

    const defaultRequestData: Partial<SystemLicense> = {
    };

    console.log(showLicenseWillNotBeChargedCB, LicenseWillNotBeCharged);

    const availableLicenses: License[] = useMemo(() => [
        {
            type: LicenseType.DETAIL_VIEW,
            text: "COM_FACPMANAGEMENT_FACP_ELS_DETAILS",
            editable: (canEditLicenses || isABF),
            value: LicenseType.DETAIL_VIEW
        },
        {
            type: LicenseType.OPERATE_AND_NOTIFICATION,
            text: "COM_FACPMANAGEMENT_FACP_ELS_USEPUSH",
            editable: (canEditLicenses || isABF),
            value: LicenseType.DETAIL_VIEW | LicenseType.OPERATE_AND_NOTIFICATION
        },
        {
            type: LicenseType.PLAN_VIEW,
            text: "COM_FACPMANAGEMENT_FACP_ELS_PLANANSICHT",
            editable: (canEditLicenses || isABF),
            value: LicenseType.DETAIL_VIEW | LicenseType.OPERATE_AND_NOTIFICATION | LicenseType.PLAN_VIEW
        },
        {
            type: LicenseType.SMS,
            text: "COM_FACPMANAGEMENT_LOGS_SMS_TEXT",
            editable: (canEditLicenses || isABF),
            value: LicenseType.SMS
        },
        {
            type: LicenseType.TRIAL_LICENSE,
            value: LicenseType.TRIAL_LICENSE | LicenseType.DETAIL_VIEW | LicenseType.OPERATE_AND_NOTIFICATION
        },
    ], [canEditLicenses, isABF]);

    const isTrialLicense = (license: LicenseType) => (license & LicenseType.TRIAL_LICENSE) === LicenseType.TRIAL_LICENSE;

    const modifySystemLicense = useCallback((data: SystemLicense) =>
    {
        _modifySystemLicense(systemId, data);
    }, [_modifySystemLicense, systemId]);

    const closeCustomerNumberPopover = useCallback(() =>
    {
        setLSTCompanyLocationPopoverIsOpen(false);
        setInvoiceAddressPopoverIsOpen(false);
    }, []);

    const handleBuyLicense = useCallback(async (isTrial: boolean = false) =>
    {
        const response = await dialog({
            title: isTrial ? t("[REACT_START_TESTVERSION]") : t("[REACT_BUY_LICENSES]"),
            message: isTrial ? t("[REACT_START_FREE_TEST_PERIOD_(3_MONTHS)]") : (systemLicense.licensePaid !== LicenseType.NONE ? t("[REACT_CONFIRM_LICENCE_CHANGES]") : t("[REACT_CONFIRM_LICENCE_BUY]")),
            areYouSureButtonTitle: t("COM_TEMPLATES_ARE_YOU_SURE"),
            confirmButtonTitle: t("COM_FACPMANAGEMENT_REGRESSION_STATUS_OK"),
            cancelButtonTitle: t("COM_BANNERS_CANCEL")
        });

        if (response) {

            const data: Partial<SystemLicense> = {
                licenseRequested: currentLicenseType,
            };

            if (isTrial) {

                const testLicense = availableLicenses.find(license => isTrialLicense(license.type));
                data.trialPeriodConsumed = true;
                data.licenseRequested = testLicense?.value;
            }

            modifySystemLicense(data as SystemLicense);
        }
    }, [
        availableLicenses,
        currentLicenseType,
        dialog,
        modifySystemLicense,
        systemLicense.licensePaid,
        t
    ]);

    const handleDeactivateLicense = useCallback(async () =>
    {
        modifySystemLicense({
            licensePaid: LicenseType.NONE,
            licenseRequested: LicenseType.NONE,
            trialPeriodConsumed: false,
            expire: null
        } as unknown as SystemLicense);

        setCurrentLicenseType(LicenseType.NONE);

    }, [modifySystemLicense]);

    const handleRequestCustomerNumber = useCallback((account: Record<string, any>) =>
    {
        if (systemLicense.customerId) {
            const data = { invoiceAccountNumber: INVOICE_ACCOUNT_TEMPORARY, invoiceAccountRequest: account } as Customer;

            if ("notifyLicenseExpiration" in account) {
                data.notifyLicenseExpiration = account.notifyLicenseExpiration;
                modifySystemLicense({
                    notify: true,
                } as SystemLicense);
            }

            modifyCustomer(systemLicense.customerId, data);
            closeCustomerNumberPopover();
        }
    }, [closeCustomerNumberPopover, modifyCustomer, modifySystemLicense, systemLicense.customerId]);

    useEffect(() =>
    {
        (async () =>
        {
            const { totalCountOfEntries } = await fetchTextMessageRecipients(systemId, {});
            setTextMessageRecipients(totalCountOfEntries);
        })();

    }, [
        systemId,
        fetchTextMessageRecipients,
        setTextMessageRecipients
    ]);

    useEffect(() =>
    {
        if (systemLicense.licenseRequested) {
            setCurrentLicenseType((pre) =>
            {
                if (pre !==

                    systemLicense.licenseRequested
                ) return systemLicense.licenseRequested!;
                return pre;
            });
        }

    }, [systemLicense.licenseRequested]);


    const handlerChoicePopoverDismiss = useCallback(() =>
    {
        setLSTCompanyLocationPopoverIsOpen(false);
    }, []);

    const handlerChoicePopoverChange = useCallback((data: Record<string, any>) =>
    {
        setCompanyLocation(data);
    }, []);

    const handlerChoicePopoverData = useCallback((data: Record<string, any>) =>
    {
        handleRequestCustomerNumber(data);
    }, [handleRequestCustomerNumber]);


    const handlerChoicePopoverOpenInvoiceAddress = useCallback(() =>
    {
        setInvoiceAddressPopoverIsOpen(true);
    }, [setInvoiceAddressPopoverIsOpen]);


    const handlerInvoiceAddressPopoverIsOpen = useCallback(() =>
    {
        setInvoiceAddressPopoverIsOpen(() => false);
    }, [setInvoiceAddressPopoverIsOpen]);


    const listLicense = (license: typeof availableLicenses[number], checked: boolean) =>
    {
        let value = currentLicenseType;
        if (license.type === LicenseType.SMS || license.type === LicenseType.PLAN_VIEW) {
            const baseLicenses = LicenseType.DETAIL_VIEW | LicenseType.OPERATE_AND_NOTIFICATION;

            value = checked ? (currentLicenseType | baseLicenses) | license.type : currentLicenseType & ~license.type;

        } else {

            value = checked
                ? license.value
                : currentLicenseType & (license.value ^ license.type);
        }
        setCurrentLicenseType(value);
    };

    function LicenseLabels(mask: number): string
    {
        return availableLicenses
            .filter(license => license.text && (mask & license.type) === license.type)
            .map(license => t(license.text!))
            .join(", ");
    }

    const addedLicenses = currentLicenseType & ~licenseRequested;
    const removedLicenses = licenseRequested & ~currentLicenseType;

    return <>
        <SettingsCol xl="8">
            <CustomerTypeAhead
                id='licenseSettings'
                disabled={!(permissionAllowed(userPermission.grantSystemAccess || 0))}
                label={t('COM_FACPMANAGEMENT_FACP_CUSTOMER_FIELD_PAYMENT_LABEL')}
                // placeHolder={systemLicense.customerNumber ? getCustomerDisplayName({ customerNumber: systemLicense.customerNumber, name: systemLicense.customerName } as Customer) : undefined}
                currentId={systemLicense.customerId || undefined}
                allowEmpty={true}
                onChange={(customerId) =>
                {
                    const data: Record<string, any> = { customerId };

                    if (!customerId) {
                        data.collectiveInvoice = false;
                    }
                    modifySystemLicense(data as SystemLicense);
                }}
            />
            <SettingsLabel
                color="medium"
                lines="none"
                text={"[REACT_CUSTOMER_NUMBER_LST]"}
                value={(systemLicense.invoiceAccountNumber || "- - - - - -") + ""} />

            <IonItem>
                {systemLicense.customerId && !validCustomerNumberLST && !deniedCustomerNumberLST ?
                    <IonButton
                        slot="end"
                        //expand="full"
                        onClick={async () => setLSTCompanyLocationPopoverIsOpen(true)}
                    >
                        {t('[REACT_REQUEST_CUSTOMER_NUMBER]')}
                    </IonButton>
                    : null}
            </IonItem>

            <SettingsCheckbox
                disabled={!systemLicense.customerId || (!userIsFromPayingCustomer && !isABF)}
                checked={systemLicense?.collectiveInvoice || false}
                onChange={async (collectiveInvoice) =>
                {
                    const x = await toggleCollectiveInvoice({ systemId, value: collectiveInvoice });
                    console.log(x);
                }}
                text={"COM_FACPMANAGEMENT_CUSTOMER_ACCOUNT_PERMISSIONS_COMMON_COLLECTIVE_INVOICE"}
            />

            <SettingsLabel
                color="medium"
                text={"[REACT_CONTRACT_DURATION]"}
                value={systemLicense.duration ? `${convertIntervalToInt(systemLicense.duration)} ${t("COM_CONTENT_MONTH")}` : ""}
            />
            <SettingsTextInput
                color="medium"
                text={"COM_FACPMANAGEMENT_LOGS_BALANCE_ORDERNR"}
                value={systemLicense.orderNumber ? systemLicense.orderNumber + "" : ""}
                onChange={(orderNumber) => modifySystemLicense({ orderNumber })}
                disabled={!(userIsFromPayingCustomer || isABF)}
            />

            <SettingsTextInput
                text={"[REACT_NOTIFICATION_BEFORE_EXPIRY]"}
                tooltip={<TooltipIcon text={t("COM_FACPMANAGEMENT_FACP_FIELD_LICENCE_DEADLINE_MAIL_DESC")} />}
                value={systemLicense.notifyLicenseExpiration || ""}
                onChange={(notifyLicenseExpiration) => modifySystemLicense({ notifyLicenseExpiration })}
                disabled={!(userIsFromPayingCustomer || isABF)}
            />

            <IonItem lines="none" >
                <IonLabel>{t("[REACT_LICENSES_OF_THE_SYSTEM]")}:</IonLabel>
                {systemLicense.expire && <LicenseCheckboxLabel
                    slot="end"
                    textLicenseActive={t('[REACT_LICENSE_GRID_VALID_UNTIL]', {
                        date: formatDate(new Date(systemLicense.expire), 'UTC', { second: undefined, minute: undefined, hour: undefined })
                    })}
                    textLicenseRequested={t('[REACT_LICENSE_GRID_VALID_AFTER]')}
                />}
            </IonItem>

            {availableLicenses.map((license, i) =>
            {
                const { editable, text, value } = license;
                if (!text) return <React.Fragment key={i}></React.Fragment>;
                const isFreeLicenseWithoutExpireDate = systemLicense.license && !systemLicense.expire;

                return systemLicense.expire ? <IonItem
                    lines='none'
                    key={i}>
                    <span>
                        <IonLabel
                            color={'medium'}
                        >{t(text)}</IonLabel>
                    </span>
                    <LicenseCheckbox
                        slot="end"
                        disabled={!editable || isTrialLicense(systemLicense.license!)}
                        licenseRequested={(currentLicenseType & value) === value}
                        licenseActive={((systemLicense.license || LicenseType.NONE) & value) === value}
                        onChange={(checked) => listLicense(license, checked)}
                    />
                </IonItem> : <SettingsCheckbox
                    key={i}
                    lines="none"
                    disabled={isFreeLicenseWithoutExpireDate || !editable || isTrialLicense(systemLicense.license!)}
                    text={text as Locale}
                    checked={isFreeLicenseWithoutExpireDate ? ((systemLicense.license || LicenseType.NONE) & value) === value : (currentLicenseType & value) === value}
                    onChange={(checked) => listLicense(license, checked)}
                />;
            })}


            {
                showLicenseWillNotBeChargedCB
                    ||
                    LicenseWillNotBeCharged ?
                    <SettingsCheckbox
                        disabled
                        lines="none"
                        text={"[REACT_DONT_CHARGE_FOR_LICENSE]"}
                        checked={LicenseWillNotBeCharged}
                    /> : null
            }

            <IonItem>
                <div

                    className={`${!mobileView ? `${style.width50}` : ""} ${style.padding5}`}
                >
                    {(canStartTestVersion || isABF)
                        /*  ||
                         (permissionAllowed(userPermission.grantSystemAccess!) && isABF) */
                        ?
                        <IonButton
                            expand="full"
                            disabled={systemLicense.trialPeriodConsumed}
                            color={systemLicense.trialPeriodConsumed ? "danger" : "success"}
                            onClick={() => handleBuyLicense(true)}
                        >
                            {t("[REACT_START_TESTVERSION]")}
                        </IonButton>
                        : null
                    }
                </div>
                <div

                    className={`${!mobileView ? `${style.width50}` : ""} ${style.padding5}`}
                >
                    <IonButton

                        expand="full"
                        disabled={
                            !(
                                (validCustomerNumberLST || isABF) &&
                                (addedLicenses > LicenseType.NONE || removedLicenses > LicenseType.NONE)
                            )
                        }
                        onClick={() =>
                        {
                            handleBuyLicense(false);
                        }}
                    >
                        {systemLicense.licensePaid !== LicenseType.NONE ? t('[REACT_SAVE_LICENSES]') : t('[REACT_BUY_LICENSES]')}
                    </IonButton>
                </div>
            </IonItem>


            {addedLicenses > LicenseType.NONE && <>

                <IonItem lines="none">
                    <IonLabel slot="start">{t("[REACT_SELECTED_LICENSES]")}:</IonLabel>
                </IonItem>


                <IonItem lines={addedLicenses > LicenseType.NONE ? "full" : "none"}>
                    <IonLabel slot="start"></IonLabel>
                    <IonLabel
                        color={"success"}
                        className={`${style.font1rem} ${style.bold}`}
                        slot="end"
                    >

                        {LicenseLabels(addedLicenses)}
                    </IonLabel>
                </IonItem>

            </>}

            {removedLicenses > LicenseType.NONE && <>

                <IonItem lines="none">
                    <IonLabel slot="start">{t("De-selected licenses")}:</IonLabel>
                </IonItem>


                <IonItem lines={removedLicenses > LicenseType.NONE ? "full" : "none"}>
                    <IonLabel slot="start"></IonLabel>
                    <IonLabel
                        color={"danger"}
                        className={`${style.font1rem} ${style.bold}`}
                        slot="end">
                        {LicenseLabels(removedLicenses)}
                    </IonLabel>
                </IonItem>

            </>}

            {currentLicenseType !== LicenseType.NONE && (userIsFromPayingCustomer || isABF) && <>

                <IonItem lines="none">
                    <IonLabel slot="start">{t("[REACT_COSTS_SELECTED_LICENSES]")}:</IonLabel>
                </IonItem>

                <IonItem>
                    <PriceListTable
                        priceListId={systemLicense.priceListId}
                        licenseType={currentLicenseType}
                        dataPoints={system.inputDevices + system.outputDevices}
                        textMessageRecipients={textMessageRecipients}
                        systemType={system.systemType}
                    />
                </IonItem>

            </>}


            {systemLicense.license !== LicenseType.NONE &&
                <><SettingsLabel color={"medium"} text={"[REACT_LICENSE_VALID_UNTIL]"} value={systemLicense ? `${isTrialLicense(systemLicense.license!) ? t('[REACT_TRIAL_LICENSE]') : ""} ${[...availableLicenses
                    .filter(license => license.text && ((systemLicense.license || LicenseType.NONE) & license.type) === license.type)
                    .map(license => t(license.text!))].join(", ")} ${systemLicense?.expire ? new Date(systemLicense.expire!).toLocaleDateString() : ""}`
                    :
                    ""
                } />
                    {(isABF) && <>
                        <IonItem
                            lines="none"
                        >
                            <IonLabel
                                position="stacked">{t('[REACT_LICENSE_MODIFY_EXPIRE]')}</IonLabel>

                            <MonthPicker
                                slot='end'
                                onChange={(expire) =>
                                {
                                    setManualExpire(expire);
                                }}
                                value={systemLicense.expire}
                            />
                            {/* <IonInput
                                style={{ maxWidth: "150px" }}
                                disabled={isTrialLicense(systemLicense.license!)}
                                slot="end"
                                ref={expireRef}
                                type="month"
                                value={systemLicense.expire ? (systemLicense.expire as unknown as string).split("-").slice(0, 2).join("-") : ""}
                            /> */}
                        </IonItem>
                        {/*  <IonItem>
                            <IonButton
                                slot="end"
                                onClick={() =>
                                {
                                    const expireMonth = expireRef.current?.value;
                                    if (expireMonth) {
                                        const expire = new Date(`${expireMonth}-01`);
                                        modifySystemLicense({ expire });
                                    }

                                }}
                            >{t('[REACT_LICENSE_MODIFY_EXPIRE]')}</IonButton>
                        </IonItem> */}
                    </>
                    }
                </>
            }
            {
                /* isABF ? <SettingsNumberInput
                    text={"[REACT_CONTRACT_DURATION]"}
                    endText=":"
                    value={convertIntervalToInt(systemLicense.duration || "P12M")}
                    onChange={(month) =>
                    {
                        // if (month <= 0) return;

                        // const expire = getExpireDate(month);
                        // const data = { ...defaultRequestData, expire } as Record<string, any>;

                        // if (!systemLicense.begin) {
                        //     data.begin = new Date().toISOString();
                        // }
                        // modifySystemLicense(data as SystemLicense);
                    }}
                />
                    : null */
            }
            {userPermission.licenseManagement ? <IonItem>
                <div
                    className={`${!mobileView ? `${style.width50}` : ""} ${style.padding5}`}
                >
                    {(permissionAllowed(userPermission.grantSystemAccess!) && isABF)
                        ? <IonButton
                            disabled={!systemLicense.license}
                            slot="end"
                            expand="full"
                            color={"danger"}
                            onClick={() =>
                            {
                                handleDeactivateLicense();
                            }}
                        >
                            {t("[REACT_LICENSE_DEACTIVATE]")}
                        </IonButton>
                        : null}
                </div>
                {userPermission.licenseManagement &&
                    <div
                        className={`${!mobileView ? `${style.width50}` : ""} ${style.padding5}`}
                    >
                        <IonButton
                            expand="full"
                            disabled={!systemLicense.license}
                            onClick={() =>
                            {
                                //const expireMonth = expireRef.current?.value;
                                const expire = manualExpire ? new Date(`${manualExpire}-01`) : null;
                                modifySystemLicense({ expire } as unknown as SystemLicense);

                            }}
                        >
                            {t("[REACT_LICENSE_MODIFY_EXPIRE]")}
                        </IonButton>
                    </div>
                }
            </IonItem>
                : null
            }
        </SettingsCol>
        <SettingsCol></SettingsCol>
        <SettingsCol></SettingsCol>


        <LSTCompanyChoicePopover
            isOpen={LSTCompanyLocationPopoverIsOpen}
            data={companyLocation}
            onDismiss={handlerChoicePopoverDismiss}
            onChange={handlerChoicePopoverChange}
            onData={handlerChoicePopoverData}
            onOpenInvoiceAddress={handlerChoicePopoverOpenInvoiceAddress}
        />

        <InvoiceAddressPopover
            isOpen={InvoiceAddressPopoverIsOpen}
            customerNumber={systemLicense.customerId}
            onDismiss={handlerInvoiceAddressPopoverIsOpen}
            onData={handleRequestCustomerNumber}
        />

    </>;
};

export default LicenseSettings;
